import { useState, useEffect, useCallback, useRef } from 'react'
import { QuantityInputState, type QuantityInputOptions } from '../lib/QuantityInputState'

export interface UseSmartQuantityInputReturn {
  displayValue: string
  shouldAutoSelect: boolean
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  reset: (newValue: number) => void
}

export const useSmartQuantityInput = (
  initialValue: number,
  onChange: (value: number) => void,
  options: QuantityInputOptions = {}
): UseSmartQuantityInputReturn => {
  const [state] = useState(() => new QuantityInputState(initialValue, options))
  const [displayValue, setDisplayValue] = useState(state.displayValue)
  const [shouldAutoSelect, setShouldAutoSelect] = useState(state.shouldAutoSelect)
  
  // Track if user has started typing (more immediate than React state)
  const hasUserTyped = useRef(false)
  
  // Reset state when initial value changes (new item added)
  useEffect(() => {
    state.reset(initialValue)
    setDisplayValue(state.displayValue)
    setShouldAutoSelect(state.shouldAutoSelect)
    hasUserTyped.current = false // Reset typing flag for new items
  }, [initialValue, state])
  
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    console.log(`🔍 handleChange: value="${value}", hasUserTyped=${hasUserTyped.current}`)
    
    // Mark that user has started typing (immediate, not dependent on React state)
    if (!hasUserTyped.current) {
      hasUserTyped.current = true
      console.log(`🔧 User started typing - setting hasUserTyped=true`)
    }
    
    state.handleChange(value, onChange)
    setDisplayValue(state.displayValue)
    setShouldAutoSelect(state.shouldAutoSelect)
    
    console.log(`🔍 After handleChange: shouldAutoSelect=${state.shouldAutoSelect}`)
    
    // Always clear selection after user has typed (use immediate flag, not React state)
    if (hasUserTyped.current) {
      console.log(`🔧 User has typed - clearing selection and positioning cursor at end`)
      setTimeout(() => {
        const input = event.target
        const length = input.value.length
        console.log(`🔧 Setting cursor position to ${length} for value "${input.value}"`)
        input.setSelectionRange(length, length)
        
        // Double-check selection was cleared
        setTimeout(() => {
          const start = input.selectionStart
          const end = input.selectionEnd
          console.log(`🔍 Selection after clear: start=${start}, end=${end}, isSelected=${start !== end}`)
        }, 10)
      }, 0)
    }
  }, [state, onChange])
  
  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const shouldSelect = state.handleFocus()
    console.log(`🔍 handleFocus: shouldSelect=${shouldSelect}, hasUserTyped=${hasUserTyped.current}`)
    
    // Only auto-select if state says we should AND user hasn't started typing yet
    if (shouldSelect && !hasUserTyped.current) {
      console.log(`🔧 Auto-selecting text on focus`)
      setTimeout(() => {
        event.target.select()
      }, 0)
    } else {
      console.log(`🔧 NOT auto-selecting text - shouldSelect=${shouldSelect}, hasUserTyped=${hasUserTyped.current}`)
    }
  }, [state])
  
  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    state.handleBlur(event.target.value, onChange)
    setDisplayValue(state.displayValue)
    setShouldAutoSelect(state.shouldAutoSelect)
  }, [state, onChange])
  
  const reset = useCallback((newValue: number) => {
    state.reset(newValue)
    setDisplayValue(state.displayValue)
    setShouldAutoSelect(state.shouldAutoSelect)
  }, [state])
  
  return {
    displayValue,
    shouldAutoSelect,
    handleChange,
    handleFocus,
    handleBlur,
    reset
  }
} 